import { useEffect, useState } from 'react';
import { Button, Flex, Modal, TextInput } from '@mantine/core';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';

interface Props {
	isOpen: boolean;
	isLoading: boolean;
	initialEmail?: string;
	onClose: () => void;
	onSubmit: (email: string) => void;
}

export const WitnessEmailModal = ({
	isOpen,
	isLoading,
	initialEmail,
	onClose,
	onSubmit,
}: Props) => {
	const tInvestor = useTranslationInvestor();
	const [email, setEmail] = useState(initialEmail ?? '');

	useEffect(() => {
		if (isOpen) {
			setEmail(initialEmail ?? '');
		}
	}, [isOpen, initialEmail]);

	const handleSubmit = () => {
		if (!email.trim()) {
			return;
		}
		onSubmit(email.trim());
	};

	const handleClose = () => {
		setEmail('');
		onClose();
	};

	return (
		<Modal
			opened={isOpen}
			onClose={handleClose}
			title={tInvestor('document_workflow.witness.modal_title')}
			centered
		>
			<Flex direction="column" gap="md" p="md">
				<TextInput
					data-testid={TEST_ELEMENT_IDS.INVESTOR_WITNESS_EMAIL_INPUT}
					label={tInvestor('document_workflow.witness.email_label')}
					placeholder={tInvestor('document_workflow.witness.email_placeholder')}
					type="email"
					value={email}
					onChange={(e) => setEmail(e.currentTarget.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							handleSubmit();
						}
					}}
				/>
				<Flex gap="sm" justify="flex-end">
					<Button variant="subtle" onClick={handleClose} disabled={isLoading}>
						{tInvestor('document_workflow.witness.cancel')}
					</Button>
					<Button
						data-testid={TEST_ELEMENT_IDS.INVESTOR_WITNESS_INVITE_SUBMIT_BUTTON}
						variant="filled"
						disabled={!email.trim() || isLoading}
						loading={isLoading}
						onClick={handleSubmit}
					>
						{tInvestor('document_workflow.witness.send')}
					</Button>
				</Flex>
			</Flex>
		</Modal>
	);
};
